/**
 * Sistema plugado (ADR 0016): abre a tela do próprio sistema já logado.
 *
 * O AURA emite um ticket curto (60 s, uso único) e o navegador faz POST dele
 * para a entrada do sistema, num formulário escondido. O ticket nunca vai na
 * URL. "quadro" mostra o sistema ocupando toda a área de conteúdo, sem barra
 * nem botão de aba nova (como o Brasíndice); "aba" é só para sistema cadastrado
 * para abrir em janela nova (aberta no clique, antes do ticket chegar, para o
 * navegador não bloquear como pop-up).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { plugAPI } from '../services/api';
import { SemAcesso } from '../components/Acesso';

export default function SistemaPlugado() {
  const { codigo } = useParams();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);
  const [envio, setEnvio] = useState(null);      // { alvo, ticket, entrada } a postar
  const formRef = useRef(null);
  const quadro = `plug-quadro-${codigo}`;

  const pedirTicket = useCallback(async () => {
    const { data } = await plugAPI.ticket(codigo);
    return data;
  }, [codigo]);

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const data = await pedirTicket();
      setDados(data);
      if (data.abrir_em === 'quadro') setEnvio({ alvo: quadro, ticket: data.ticket, entrada: data.entrada });
    } catch (e) {
      setErro(e.response?.status === 403 ? (e.response?.data?.detail || 'Sem acesso') : 'Não foi possível abrir o sistema agora.');
    }
  }, [pedirTicket, quadro]);

  useEffect(() => { setDados(null); setEnvio(null); carregar(); }, [carregar]);

  // Posta o ticket assim que o formulário estiver montado com os valores certos.
  useEffect(() => { if (envio && formRef.current) formRef.current.submit(); }, [envio]);

  const abrirEmAba = async () => {
    const janela = `plug-aba-${codigo}-${Date.now()}`;
    window.open('about:blank', janela);
    try {
      const data = await pedirTicket();
      setEnvio({ alvo: janela, ticket: data.ticket, entrada: data.entrada });
    } catch (e) {
      setErro(e.response?.data?.detail || 'Não foi possível abrir o sistema agora.');
    }
  };

  if (erro) return <SemAcesso motivo={erro} />;
  if (!dados) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 96px)', margin: '-8px -8px 0' }}>
      {dados.abrir_em === 'quadro' ? (
        <iframe
          name={quadro}
          title={dados.nome}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads allow-modals"
          style={{ flex: 1, width: '100%', border: 'none', background: '#fff' }}
        />
      ) : (
        <div className="empty-state" style={{ marginTop: '10vh' }}>
          <p>{dados.nome} abre numa aba nova, já com o seu acesso.</p>
          <button className="btn btn-primary" onClick={abrirEmAba}><ExternalLink size={16} /> Abrir {dados.nome}</button>
        </div>
      )}

      {envio && (
        <form ref={formRef} key={`${envio.alvo}-${envio.ticket.slice(-12)}`} method="POST" action={envio.entrada}
              target={envio.alvo} style={{ display: 'none' }}>
          <input type="hidden" name="ticket" value={envio.ticket} />
        </form>
      )}
    </div>
  );
}
